/**
 * Curated, region-grouped list of IANA timezones used throughout the app.
 *
 * Picked for the indie-musician audience: thorough Latin American coverage first, then
 * the rest of the Americas, Europe, Asia, Oceania, and Africa. Labels are user-friendly
 * city names so people don't have to know that "Mexico City" lives under
 * "America/Mexico_City".
 */
export interface TimezoneOption {
  /** IANA id stored on the backend. */
  id: string;
  /** Friendly label shown in the dropdown. */
  label: string;
}

export interface TimezoneGroup {
  region: string;
  zones: TimezoneOption[];
}

export const TIMEZONE_GROUPS: TimezoneGroup[] = [
  {
    region: "Latin America",
    zones: [
      { id: "America/Mexico_City", label: "Mexico City (CDMX)" },
      { id: "America/Tijuana", label: "Tijuana" },
      { id: "America/Cancun", label: "Cancún" },
      { id: "America/Guatemala", label: "Guatemala" },
      { id: "America/El_Salvador", label: "San Salvador" },
      { id: "America/Tegucigalpa", label: "Tegucigalpa" },
      { id: "America/Managua", label: "Managua" },
      { id: "America/Costa_Rica", label: "San José (CR)" },
      { id: "America/Panama", label: "Panamá" },
      { id: "America/Havana", label: "La Habana" },
      { id: "America/Santo_Domingo", label: "Santo Domingo" },
      { id: "America/Puerto_Rico", label: "San Juan (PR)" },
      { id: "America/Bogota", label: "Bogotá" },
      { id: "America/Caracas", label: "Caracas" },
      { id: "America/Lima", label: "Lima" },
      { id: "America/Guayaquil", label: "Quito / Guayaquil" },
      { id: "America/La_Paz", label: "La Paz" },
      { id: "America/Asuncion", label: "Asunción" },
      { id: "America/Santiago", label: "Santiago" },
      { id: "America/Argentina/Buenos_Aires", label: "Buenos Aires" },
      { id: "America/Montevideo", label: "Montevideo" },
      { id: "America/Sao_Paulo", label: "São Paulo" },
      { id: "America/Manaus", label: "Manaus" },
      { id: "America/Recife", label: "Recife" },
    ],
  },
  {
    region: "North America",
    zones: [
      { id: "America/New_York", label: "New York" },
      { id: "America/Chicago", label: "Chicago" },
      { id: "America/Denver", label: "Denver" },
      { id: "America/Phoenix", label: "Phoenix" },
      { id: "America/Los_Angeles", label: "Los Angeles" },
      { id: "America/Anchorage", label: "Anchorage" },
      { id: "Pacific/Honolulu", label: "Honolulu" },
      { id: "America/Toronto", label: "Toronto" },
      { id: "America/Vancouver", label: "Vancouver" },
    ],
  },
  {
    region: "Europe",
    zones: [
      { id: "Europe/London", label: "London" },
      { id: "Europe/Madrid", label: "Madrid" },
      { id: "Europe/Paris", label: "Paris" },
      { id: "Europe/Berlin", label: "Berlin" },
      { id: "Europe/Rome", label: "Rome" },
      { id: "Europe/Amsterdam", label: "Amsterdam" },
      { id: "Europe/Lisbon", label: "Lisbon" },
      { id: "Europe/Athens", label: "Athens" },
      { id: "Europe/Istanbul", label: "Istanbul" },
      { id: "Europe/Moscow", label: "Moscow" },
    ],
  },
  {
    region: "Asia",
    zones: [
      { id: "Asia/Dubai", label: "Dubai" },
      { id: "Asia/Jerusalem", label: "Jerusalem" },
      { id: "Asia/Kolkata", label: "Mumbai / Delhi" },
      { id: "Asia/Bangkok", label: "Bangkok" },
      { id: "Asia/Singapore", label: "Singapore" },
      { id: "Asia/Hong_Kong", label: "Hong Kong" },
      { id: "Asia/Shanghai", label: "Shanghai" },
      { id: "Asia/Seoul", label: "Seoul" },
      { id: "Asia/Tokyo", label: "Tokyo" },
    ],
  },
  {
    region: "Oceania",
    zones: [
      { id: "Australia/Perth", label: "Perth" },
      { id: "Australia/Sydney", label: "Sydney" },
      { id: "Australia/Melbourne", label: "Melbourne" },
      { id: "Pacific/Auckland", label: "Auckland" },
    ],
  },
  {
    region: "Africa",
    zones: [
      { id: "Africa/Casablanca", label: "Casablanca" },
      { id: "Africa/Lagos", label: "Lagos" },
      { id: "Africa/Cairo", label: "Cairo" },
      { id: "Africa/Johannesburg", label: "Johannesburg" },
    ],
  },
  {
    region: "Other",
    zones: [{ id: "UTC", label: "UTC" }],
  },
];

/**
 * Flat list of all known timezone ids. Useful for legacy lookups when we only have an id.
 */
export const ALL_TIMEZONE_IDS = TIMEZONE_GROUPS.flatMap((g) => g.zones.map((z) => z.id));

/**
 * Look up a friendly label by IANA id. Falls back to the id if it's not in our curated list
 * (custom user input, future zones, etc.).
 */
export function timezoneLabel(id: string): string {
  for (const group of TIMEZONE_GROUPS) {
    const match = group.zones.find((z) => z.id === id);
    if (match) return match.label;
  }
  return id;
}
